import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { 
  Home, 
  ShoppingBag, 
  Users, 
  DollarSign, 
  Package, 
  Settings, 
  FileText, 
  ShoppingCart, 
  Clock, 
  Mail,
  UserPlus,
  UserCheck,
  Calendar,
  Lock,
  Shield,
  BarChart2,
  User,
  XCircle,
  Grid3X3
} from 'lucide-react';
import adminService from '../../services/adminService';
import DashboardChart from '../DashboardChart';
import userRoleUtils from '../../utils/userRoleUtils';

// Import our components
import DashboardCard from './DashboardCard';
import CardSlider from './CardSlider';
import InquiriesSection from './InquiriesSection';
import RecentOrdersSection from './RecentOrdersSection';
import ChartSection from './ChartSection';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [inquirySearchTerm, setInquirySearchTerm] = useState('');
  const [dashboardData, setDashboardData] = useState({
    products: 0,
    properties: 0,
    orders: 0,
    users: 0,
    payments: 0,
    activeOrders: 0,
    todayOrders: 0,
    cancelledOrders: 0,
    unapprovedUsers: 0,
    categories: 0,
    inquiries: [],
    totalPacks: 0,
    outForUse: [],
    missingItems: [],
    graphData: {
      sales: [],
      inventories: [],
      days: []
    },
    orderData: {
      active: 0,
      completed: 0,
      failed: 0
    },
    propertyData: {
      active: 0,
      inactive: 0
    }
  });

  // Check if current user is admin
  const isAdmin = userRoleUtils.isAdmin();

  // Function to get dashboard data from API
  const getDashboardData = async () => {
    try {
      setIsLoading(true);
      let data = await adminService.getDashboardData();
      
      // Process inquiry data from the array structure
      const processedInquiries = Array.isArray(data.inquiry_data) 
        ? data.inquiry_data.map((inquiry, index) => {
            return {
              key: index,
              email: inquiry[0] || 'N/A',
              name: inquiry[1] || 'N/A',
              phone: inquiry[2] || 'N/A',
              category: inquiry[3] || 'N/A',
              message: inquiry[4] || 'N/A',
              source: inquiry[12] || 'N/A',
              date: inquiry[9] || 'N/A',
              time: inquiry[10] || 'N/A',
              ip: inquiry[6] || 'N/A'
            };
          })
        : [];

      const totalOrders = Number(data.orders || 0);
      const activeOrders = Number(data.activeorder || 0);
      const failedOrders = Number(data.failedorder || 0);
      
      // Map API data to our state structure
      setDashboardData({
        products: data.products || 0,
        properties: data.properties || 0,
        orders: totalOrders,
        users: data.users || 0,
        payments: data.payments || 0,
        activeOrders,
        todayOrders: data.todayorder || 0,
        cancelledOrders: data.cancelledorder || failedOrders,
        unapprovedUsers: data.unapprovedUser || 0,
        categories: data.categories || 0,
        inquiries: processedInquiries,
        totalPacks: data.totalcount || 0,
        outForUse: data.outforuse || [],
        chartOrders: data.chartorders || [],
        missingItems: data.missingitems || [],
        graphData: {
          sales: JSON.parse(data.graphs_data?.sales || '[]'),
          inventories: JSON.parse(data.graphs_data?.inventories || '[]'),
          days: JSON.parse(data.graphs_data?.fdata || '[]')
        },
        orderData: {
          active: activeOrders,
          completed: Math.max(totalOrders - activeOrders - failedOrders, 0),
          failed: failedOrders
        },
        propertyData: {
          active: data.activeProperties || 0,
          inactive: data.inactiveProperties || 0
        }
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to load dashboard data'
      });
      
      setDashboardData({
        products: 0,
        properties: 0,
        orders: 0,
        users: 0,
        payments: 0,
        activeOrders: 0,
        todayOrders: 0,
        cancelledOrders: 0,
        unapprovedUsers: 0,
        categories: 0,
        inquiries: [],
        totalPacks: 0,
        outForUse: [],
        chartOrders: [],
        missingItems: [],
        graphData: {
          sales: [],
          inventories: [],
          days: []
        },
        orderData: {
          active: 0,
          completed: 0,
          failed: 0
        },
        propertyData: {
          active: 0,
          inactive: 0
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter inquiries based on search term
  const filteredInquiries = dashboardData.inquiries.filter(inquiry => {
    const searchTerm = inquirySearchTerm.toLowerCase();
    return (
      inquiry.email.toLowerCase().includes(searchTerm) ||
      inquiry.name.toLowerCase().includes(searchTerm) ||
      inquiry.phone.toLowerCase().includes(searchTerm) ||
      inquiry.category.toLowerCase().includes(searchTerm) ||
      inquiry.message.toLowerCase().includes(searchTerm) ||
      inquiry.date.toLowerCase().includes(searchTerm)
    );
  });

  // Load dashboard data on component mount
  useEffect(() => {
    getDashboardData();
  }, []);

  // Orders data for charts
  const chartOrders = Array.isArray(dashboardData.chartOrders)
    ? dashboardData.chartOrders
    : [];
  const recentOrders = Array.isArray(dashboardData.outForUse)
    ? dashboardData.outForUse
    : [];

  // Navigation function
  const navigateTo = (path) => {
    navigate(path);
  };

  // Define card data arrays with different accent colors
  const ordersStatusCards = [
    { 
      icon: ShoppingCart, 
      title: "All Orders", 
      count: dashboardData.orders, 
      accentColor: "#3b82f6", // blue
      onClick: () => navigateTo('/admin/all-orders')
    },
    { 
      icon: Calendar, 
      title: "Today's Orders", 
      count: dashboardData.todayOrders, 
      accentColor: "#f97316", // orange
      onClick: () => navigateTo('/admin/all-orders?date=today')
    },
    { 
      icon: Clock, 
      title: "Active Orders", 
      count: dashboardData.activeOrders, 
      accentColor: "#10b981", // emerald
      onClick: () => navigateTo('/admin/all-orders?status=active')
    },
    { 
      icon: XCircle, 
      title: "Cancelled Orders", 
      count: dashboardData.cancelledOrders, 
      accentColor: "#ef4444", // red
      onClick: () => navigateTo('/admin/all-orders?status=cancelled')
    }
  ];

  const mainManagementCards = [
    { 
      icon: Home, 
      title: "All Properties", 
      count: dashboardData.properties, 
      accentColor: "#10b981", // emerald
      onClick: () => navigateTo('/admin/properties')
    },
    { 
      icon: ShoppingBag, 
      title: "All Products", 
      count: dashboardData.products, 
      accentColor: "#f59e0b", // amber
      onClick: () => navigateTo('/admin/all-products')
    },
    { 
      icon: Grid3X3, 
      title: "Categories", 
      count: dashboardData.categories, 
      accentColor: "#8b5cf6", // violet
      onClick: () => navigateTo('/admin/categories')
    },
    { 
      icon: Settings, 
      title: "Settings", 
      count: "Config", 
      accentColor: "#475569", // slate
      onClick: () => navigateTo('/admin/settings')
    }
  ];

  const systemManagementCards = [
    { 
      icon: BarChart2, 
      title: "Reports", 
      count: "6", // Hard-coded value
      accentColor: "#0d9488", // teal
      onClick: () => navigateTo('/admin/reports/sales')
    },
    { 
      icon: Mail, 
      title: "Messages", 
      count: dashboardData.inquiries.length, 
      accentColor: "#6366f1", // indigo
      onClick: () => navigateTo('/admin/messages')
    },
    { 
      icon: User, 
      title: "User Group", 
      count: "7", // Hard-coded value
      accentColor: "#2563eb", // blue
      onClick: () => navigateTo('/admin/user-groups')
    },
    { 
      icon: DollarSign, 
      title: "Payments", 
      count: dashboardData.payments, 
      accentColor: "#8b5cf6", // violet
      onClick: () => navigateTo('/admin/payments')
    }
  ];

  const userManagementCards = [
    { 
      icon: Users, 
      title: "All Users", 
      count: dashboardData.users, 
      accentColor: "#06b6d4", // cyan
      onClick: () => navigateTo('/admin/all-users')
    },
    { 
      icon: UserPlus, 
      title: "Waiting for Approval", 
      count: dashboardData.unapprovedUsers, 
      badge: dashboardData.unapprovedUsers > 0 ? {
        text: 'Needs Action',
        color: 'red'
      } : null,
      accentColor: "#eab308", // yellow
      onClick: () => navigateTo('/admin/waiting-users')
    },
    { 
      icon: Shield, 
      title: "Roles", 
      count: "4", // Hard-coded value
      accentColor: "#2563eb", // blue
      onClick: () => navigateTo('/admin/roles')
    },
    { 
      icon: Lock, 
      title: "Permission", 
      count: "18", // Hard-coded value
      accentColor: "#64748b", // slate
      onClick: () => navigateTo('/admin/permissions')
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-12 w-12 border-t-2 border-b-2 border-golden-navy rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-golden-navy mb-6">Admin Dashboard</h1>

      {/* Charts Section */}
      <ChartSection title="Orders Activity">
        <DashboardChart orders={chartOrders} />
      </ChartSection>
      
      
      {/* Orders & Status Cards Slider */}
      <CardSlider 
        title="Orders & Status" 
        icon={ShoppingCart}
      >
        {ordersStatusCards.map((card, index) => (
          <DashboardCard
            key={`order-${index}`}
            icon={card.icon}
            title={card.title}
            count={card.count}
            badge={card.badge}
            accentColor={card.accentColor}
            onClick={card.onClick}
          />
        ))}
      </CardSlider>
      
      {/* Main Management Cards Slider */}
      <CardSlider 
        title="Main Management" 
        icon={ShoppingBag}
      >
        {mainManagementCards.map((card, index) => (
          <DashboardCard
            key={`main-${index}`}
            icon={card.icon}
            title={card.title}
            count={card.count}
            badge={card.badge}
            accentColor={card.accentColor}
            onClick={card.onClick}
          />
        ))}
      </CardSlider>
      
      {/* System Management Cards Slider */}
      <CardSlider 
        title="System Management" 
        icon={Settings}
      >
        {systemManagementCards.map((card, index) => (
          <DashboardCard
            key={`system-${index}`}
            icon={card.icon}
            title={card.title}
            count={card.count}
            badge={card.badge}
            accentColor={card.accentColor}
            onClick={card.onClick}
          />
        ))}
      </CardSlider>
      
      {/* User Management Cards Slider */}
      <CardSlider 
        title="User Management" 
        icon={Users}
      >
        {userManagementCards.map((card, index) => (
          <DashboardCard
            key={`user-${index}`}
            icon={card.icon}
            title={card.title}
            count={card.count}
            badge={card.badge}
            accentColor={card.accentColor}
            onClick={card.onClick}
          />
        ))}
      </CardSlider>
      
     
      {/* Recent Orders Section */}
      <RecentOrdersSection orders={recentOrders} loading={isLoading} />
      
      {/* Admin-Only Inquiries Section */}

    </div>
  );
};

export default AdminDashboard;
